import {
  createGlobalState,
  createSharedComposable,
  useDebounceFn,
  breakpointsTailwind,
  useBreakpoints,
} from "@vueuse/core";
import { omit, sumBy } from "lodash";
import { computed, readonly, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { createOrderFromCart as _createOrderFromCart, logClientInformation } from "@/core/api/graphql";
import { updateResellerPreference } from "@/core/api/graphql/account/mutations/updateResellerPreference";
import { useCalculateUsdSalesTaxMutation } from "@/core/api/graphql/cart/mutations/calculateUsdSalesTax";
import { useCheckoutInitMutation } from "@/core/api/graphql/cart/mutations/checkoutInit";
import { useSetIntendsToExportMutation } from "@/core/api/graphql/cart/mutations/setIntendsToExport";
import { useSetWantsInvoicePdfMutation } from "@/core/api/graphql/cart/mutations/setWantsInvoicePdf";
import { useSetWantsProformaInvoiceMutation } from "@/core/api/graphql/cart/mutations/setWantsProformaInvoice";
import { GraphQLErrorCode } from "@/core/api/graphql/enums";
import { createOrderFromCartWithPaymentAuthorization as _createOrderFromCartWithPaymentAuthorization } from "@/core/api/graphql/orders/mutations/createOrderFromCartWithPaymentAuthorization";
import { createOrderFromQuote as _createOrderFromQuote } from "@/core/api/graphql/orders/mutations/createOrderFromQuote";
import { createOrderFromQuoteWithPaymentAuthorization as _createOrderFromQuoteWithPaymentAuthorization } from "@/core/api/graphql/orders/mutations/createOrderFromQuoteWithPaymentAuthorization";
import { useUpdateCustomerOrderCommentMutation } from "@/core/api/graphql/orders/mutations/updateCustomerOrderComment";
import { useUpdateCustomerOrderIntendsToExportMutation } from "@/core/api/graphql/orders/mutations/updateCustomerOrderIntendsToExport";
import { useUpdateCustomerOrderPurchaseOrderNumberMutation } from "@/core/api/graphql/orders/mutations/updateCustomerOrderPurchaseOrderNumber";
import { useUpdateCustomerOrderWantsInvoicePdfMutation } from "@/core/api/graphql/orders/mutations/updateCustomerOrderWantsInvoicePdf";
import { useUpdateCustomerOrderWantsProformaInvoiceMutation } from "@/core/api/graphql/orders/mutations/updateCustomerOrderWantsProformaInvoice";
import { isCartProductUnavailableError, isErrorWithCode } from "@/core/api/graphql/utils";
import { useThemeContext, useGeolocation, useCountriesWithoutEmbargoed } from "@/core/composables";
import { useDataAreaId } from "@/core/composables/useDataAreaId";
import { useFooterBlock } from "@/core/composables/useFooterBlock";
import { AddressType, ProductType } from "@/core/enums";
import { globals } from "@/core/globals";
import { isEqualAddresses, Logger } from "@/core/utilities";
import { notifyBrlCurrencyOrderError } from "@/core/utilities/notifyBrlCurrencyOrderError";
import { useUser, useUserAddresses, useUserOrder } from "@/shared/account";
import { useThorlabsAlgoliaAnalytics } from "@/shared/algolia-analytics/algolia-analytics";
import { useFullCart, EXTENDED_DEBOUNCE_IN_MS, getAvailability } from "@/shared/cart";
import { useSlugInfo } from "@/shared/common/composables/useSlugInfo";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { useNotifications } from "@/shared/notification";
import { PaymentMethodGroupType } from "@/shared/payment";
import { eventBus } from "@/shared/thorlabs/shared";
import { ORDER_ERROR_HANDLED } from "../types";
import type { OrderErrorHandledType } from "../types";
import type {
  CustomerOrderTypeExtension,
  InputAddressTypeExtension,
  InputCalculateUsdTaxType,
  InputPaymentTypeExtension,
  MemberAddressTypeExtension,
  PaymentMethodType,
  ShippingMethodType,
  InputCheckoutInitType,
  CreateOrderWithPaymentAuthorizationResultType,
  KeyValueType,
  OrderLineItemTypeExtension,
  InputShipmentTypeExtension,
  CartExtension,
} from "@/core/api/graphql/types";
import type { OrderLineItemWithAvailabilityStatus } from "@/shared/cart";
import type { IPaymentOutputStateDetails } from "@/shared/payment";
import type { IShippingMethodBlock } from "@/shared/thorlabs/blocks/types";
const { getDataAreaId, regionCurrency } = useDataAreaId();
const useGlobalCheckout = createGlobalState(() => {
  const loading = ref(false);
  const billingAddressEqualsShipping = ref(false);

  const placedOrder = shallowRef<CustomerOrderTypeExtension | null>(null);

  const _comment = ref<string>();
  const _purchaseOrderNumber = ref<string>();
  const _intendsToExport = ref<boolean>();
  const _wantsInvoicePdf = ref<boolean>();
  const _wantsProformaInvoice = ref<boolean>();
  const shippingAccountNumberChanging = ref(false);
  const _shippingAccountNumber = ref<string>();
  const calculateUsdSalesTaxChanging = ref(false);
  const deliveryAddressListUpdaterKey = ref(0);
  const hasCompletedExportStep = ref(false);

  function clearState() {
    _comment.value = undefined;
    _purchaseOrderNumber.value = undefined;
    _intendsToExport.value = undefined;
    _wantsInvoicePdf.value = undefined;
    _wantsProformaInvoice.value = undefined;
    _shippingAccountNumber.value = undefined;
    billingAddressEqualsShipping.value = false;
    hasCompletedExportStep.value = false;
  }
  return {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    _comment,
    _purchaseOrderNumber,
    _intendsToExport,
    _wantsInvoicePdf,
    _wantsProformaInvoice,
    shippingAccountNumberChanging,
    _shippingAccountNumber,
    calculateUsdSalesTaxChanging,
    deliveryAddressListUpdaterKey,
    hasCompletedExportStep,
    clearState,
  };
});

export function _useCheckout() {
  // const ga = useGoogleAnalytics();
  const { t } = useI18n();
  const notifications = useNotifications();
  const router = useRouter();
  const route = useRoute();
  const { isAuthenticated, user } = useUser();
  const { addresses: personalAddresses, fetchAddresses: fetchPersonalAddresses } = useUserAddresses();
  const { order: quoteOrder, fetchFullOrder } = useUserOrder();
  const { countriesWithoutEmbargoed, loadCountriesWithoutEmbargoed } = useCountriesWithoutEmbargoed();

  const {
    cart,
    selectedItemIds,
    shipment,
    payment,
    availableShippingMethods,
    availablePaymentMethods,
    hasValidationErrors,
    updateShipment,
    updatePayment,
    changeComment,
    updatePurchaseOrderNumber,
    cartWeight,
    setUpdatedCart,
  } = useFullCart();
  const {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    _comment,
    _purchaseOrderNumber,
    _intendsToExport,
    _wantsInvoicePdf,
    _wantsProformaInvoice,
    shippingAccountNumberChanging,
    _shippingAccountNumber,
    calculateUsdSalesTaxChanging,
    deliveryAddressListUpdaterKey,
    hasCompletedExportStep,
    clearState: clearGlobalCheckoutState,
  } = useGlobalCheckout();
  const { themeContext } = useThemeContext();
  const userSelectedAddress = ref<string>();
  const userSelectedBillingAddress = ref<string>();
  const checkoutMode = ref<string>();
  const hasCheckoutCompleted = ref(false);
  const paymentMethodDetailsForReview = ref<IPaymentOutputStateDetails>();
  const isPaymentMethodExpanded = ref(false);
  const selectedPaymentMethod = ref("");
  const deliveryAddress = computed(() => shipment.value?.deliveryAddress);
  const shipmentMethodTopNote = ref<string>();
  const loadingShippingChoices = ref(false);
  const isRequestQuoteFlow = computed(() => route.path.includes("/quote"));
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isMobile = breakpoints.smaller("md");
  const { gtmSendPurchaseEvent, gtmSendQuoteEvent } = useThorlabsGtm();
  const { algoliaSendPurchasedObjectIDsAfterSearch } = useThorlabsAlgoliaAnalytics();
  const customerOrderAvailabilityItems = ref<OrderLineItemWithAvailabilityStatus[]>([]);

  const billingAddress = computed(() =>
    billingAddressEqualsShipping.value && deliveryAddress.value ? deliveryAddress.value : payment.value?.billingAddress,
  );

  const shipmentMethod = computed(() =>
    availableShippingMethods.value.find(
      (item) => item.id === shipment.value?.shipmentMethodCode + "_" + shipment.value?.shipmentMethodOption,
    ),
  );
  const paymentMethod = computed(() =>
    availablePaymentMethods.value.find((item) => item.code === payment.value?.paymentGatewayCode),
  );

  const changeCommentDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.comment !== value) {
      await changeComment(value);
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updatePurchaseOrderNumberDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.purchaseOrderNumber !== value) {
      await updatePurchaseOrderNumber(value);
    }
  }, EXTENDED_DEBOUNCE_IN_MS);
  const setIntendsToExportMutation = useSetIntendsToExportMutation();
  const setWantsInvoicePdfMutation = useSetWantsInvoicePdfMutation();
  const setWantsProformaInvoiceMutation = useSetWantsProformaInvoiceMutation();
  const calculateUsdSalesTaxMutation = useCalculateUsdSalesTaxMutation();
  const checkoutInit = useCheckoutInitMutation();

  const updateCustomerOrderIntendsToExportMutation = useUpdateCustomerOrderIntendsToExportMutation();
  const updateCustomerOrderCommentMutation = useUpdateCustomerOrderCommentMutation();
  const updateCustomerOrderPurchaseOrderNumberMutation = useUpdateCustomerOrderPurchaseOrderNumberMutation();
  const updateCustomerOrderWantsInvoicePdfMutation = useUpdateCustomerOrderWantsInvoicePdfMutation();
  const updateCustomerOrderWantsProformaInvoiceMutation = useUpdateCustomerOrderWantsProformaInvoiceMutation();
  const { footerBlock } = useFooterBlock();

  async function finalizeResellerPreference() {
    if (user.value?.contact && !user.value.contact.hasAnsweredResellerQuestionTL) {
      try {
        const { countryCodeISO2 } = useGeolocation();
        await updateResellerPreference({
          userId: user.value.id ?? "",
          isReseller: user.value.contact.isResellerTL ?? false,
          salesEmail: footerBlock?.value?.salesEmail ?? "",
          markAsAnswered: true,
          geolocationCountryCode: countryCodeISO2.value,
        });
        user.value.contact.hasAnsweredResellerQuestionTL = true;
      } catch (e) {
        Logger.error("Failed to finalize reseller preference", e);
      }
    }
  }

  const updateIntendsToExportDebounced = useDebounceFn(async (value: boolean) => {
    if (cart.value?.intendsToExportTL !== value) {
      await setIntendsToExportMutation.mutate({ command: { intendsToExportTL: value } });
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateWantsInvoicePdfDebounced = useDebounceFn(async (value: boolean) => {
    if (cart.value?.wantsInvoicePdfTL !== value) {
      await setWantsInvoicePdfMutation.mutate({ command: { wantsInvoicePdfTL: value } });
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateWantsProformaInvoiceDebounced = useDebounceFn(async (value: boolean) => {
    if (cart.value?.wantsProformaInvoiceTL !== value) {
      await setWantsProformaInvoiceMutation.mutate({ command: { wantsProformaInvoiceTL: value } });
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateCustomerOrderCommentDebounced = useDebounceFn(async (orderId: string, value: string) => {
    if (placedOrder.value?.comment !== value) {
      try {
        await updateCustomerOrderCommentMutation.mutate({
          command: { orderId, comment: value },
        });
      } catch (error) {
        Logger.error("Failed to update customer order comment", error);
      }
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateCustomerOrderIntendsToExportDebounced = useDebounceFn(async (orderId: string, value: boolean) => {
    if (quoteOrder.value?.intendsToExportTL !== value) {
      try {
        await updateCustomerOrderIntendsToExportMutation.mutate({
          command: { orderId, intendsToExportTL: value },
        });
      } catch (error) {
        Logger.error("Failed to update customer order intends to export", error);
      }

      await fetchFullOrder({ id: quoteOrder.value!.id });
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateCustomerOrderWantsInvoicePdfDebounced = useDebounceFn(async (orderId: string, value: boolean) => {
    if (quoteOrder.value?.wantsInvoicePdfTL !== value) {
      try {
        await updateCustomerOrderWantsInvoicePdfMutation.mutate({
          command: { orderId, wantsInvoicePdfTL: value },
        });
      } catch (error) {
        Logger.error("Failed to update customer order intends to export", error);
      }
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateCustomerOrderWantsProformaInvoiceDebounced = useDebounceFn(async (orderId: string, value: boolean) => {
    if (quoteOrder.value?.wantsProformaInvoiceTL !== value) {
      try {
        await updateCustomerOrderWantsProformaInvoiceMutation.mutate({
          command: { orderId, wantsProformaInvoiceTL: value },
        });
      } catch (error) {
        Logger.error("Failed to update customer order intends to export", error);
      }
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updateCustomerOrderPurchaseOrderNumberDebounced = useDebounceFn(async (orderId: string, value: string) => {
    if (quoteOrder.value?.purchaseOrderNumber !== value) {
      try {
        loading.value = true;
        await updateCustomerOrderPurchaseOrderNumberMutation.mutate({
          command: { orderId, purchaseOrderNumber: value },
        });
      } catch (error) {
        loading.value = false;
        Logger.error("Failed to update customer order intends to export", error);
      }
      loading.value = false;
      await fetchFullOrder({ id: quoteOrder.value!.id });
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  const hasExplicitlyCalculatedTax = ref(false);

  async function updateUsdSalesTax(command: InputCalculateUsdTaxType) {
    calculateUsdSalesTaxChanging.value = true;

    const response = await calculateUsdSalesTaxMutation.mutate({ command });
    if (response?.data?.calculateSalesTax) {
      setUpdatedCart(response.data.calculateSalesTax as CartExtension);
      hasExplicitlyCalculatedTax.value = true; // set to true when explicitly triggered
    }

    calculateUsdSalesTaxChanging.value = false;
  }

  async function checkoutInitialize() {
    loading.value = true;
    const { countryCodeISO2 } = useGeolocation();
    const { dataAreaId } = useDataAreaId();

    const command: InputCheckoutInitType = {
      cartId: cart.value?.id,
      currencyCode: globals.currencyCode,
      cultureName: globals.cultureName,
      storeId: globals.storeId,
      userId: globals.userId,
      memberId: user.value?.memberId,
      countryCode: countryCodeISO2.value,
      dataAreaId: dataAreaId.value,
      isQuote: isRequestQuoteFlow.value,
    };

    const response = await checkoutInit.mutate({ command });

    // Explicitly update local cart state with mutation response
    if (response?.data?.checkoutInit) {
      setUpdatedCart(response.data.checkoutInit as CartExtension);
    }
    loading.value = false;
  }

  const commentUpdate = ref<Promise<void> | null>(null);

  const comment = computed({
    get: () => _comment.value ?? cart.value?.comment ?? "",
    set: (value: string) => {
      _comment.value = value;
      commentUpdate.value = changeCommentDebounced(value);
    },
  });

  const purchaseOrderNumberUpdate = ref<Promise<void> | null>(null);

  const purchaseOrderNumber = computed({
    get: () => _purchaseOrderNumber.value ?? cart.value?.purchaseOrderNumber ?? "",
    set: (value: string) => {
      _purchaseOrderNumber.value = value;
      purchaseOrderNumberUpdate.value = updatePurchaseOrderNumberDebounced(value);
    },
  });

  const intendsToExportUpdate = ref<Promise<void> | null>(null);

  const intendsToExport = computed({
    get: () => _intendsToExport.value ?? cart.value?.intendsToExportTL ?? false,
    set: (value: boolean) => {
      _intendsToExport.value = value;
      intendsToExportUpdate.value = updateIntendsToExportDebounced(value);
    },
  });

  const customerOrderIntendsToExport = computed({
    get: () => quoteOrder.value?.intendsToExportTL ?? false,
    set: (value: boolean) => {
      void updateCustomerOrderIntendsToExportDebounced(quoteOrder.value!.id, value);
    },
  });

  const customerOrderComment = computed({
    get: () => quoteOrder.value?.comment ?? "",
    set: (value: string) => {
      void updateCustomerOrderCommentDebounced(quoteOrder.value!.id, value);
    },
  });

  const customerOrderWantsInvoicePdf = computed({
    get: () => quoteOrder.value?.wantsInvoicePdfTL ?? false,
    set: (value: boolean) => {
      void updateCustomerOrderWantsInvoicePdfDebounced(quoteOrder.value!.id, value);
    },
  });

  const customerOrderWantsProformaInvoice = computed({
    get: () => quoteOrder.value?.wantsProformaInvoiceTL ?? false,
    set: (value: boolean) => {
      void updateCustomerOrderWantsProformaInvoiceDebounced(quoteOrder.value!.id, value);
    },
  });

  const customerOrderPurchaseOrderNumberUpdate = ref<Promise<void> | null>(null);

  const customerOrderPurchaseOrderNumber = computed({
    get: () => quoteOrder.value?.purchaseOrderNumber ?? "",
    set: (value: string) => {
      customerOrderPurchaseOrderNumberUpdate.value = updateCustomerOrderPurchaseOrderNumberDebounced(
        quoteOrder.value!.id,
        value,
      );
    },
  });

  const wantsInvoicePdfUpdate = ref<Promise<void> | null>(null);

  const wantsInvoicePdf = computed({
    get: () => _wantsInvoicePdf.value ?? cart.value?.wantsInvoicePdfTL ?? false,
    set: (value: boolean) => {
      _wantsInvoicePdf.value = value;
      wantsInvoicePdfUpdate.value = updateWantsInvoicePdfDebounced(value);
    },
  });

  const wantsProformaInvoiceUpdate = ref<Promise<void> | null>(null);

  const wantsProformaInvoice = computed({
    get: () => _wantsProformaInvoice.value ?? cart.value?.wantsProformaInvoiceTL ?? false,
    set: (value: boolean) => {
      _wantsProformaInvoice.value = value;
      wantsProformaInvoiceUpdate.value = updateWantsProformaInvoiceDebounced(value);
    },
  });

  const shippingAccountNumberRecalculateTotals = computed({
    get: () => _shippingAccountNumber.value ?? shipment.value?.shippingAccountNumberTL ?? "",
    set: (value: string) => {
      _shippingAccountNumber.value = value;
    },
  });

  const isValidDeliveryAddress = computed<boolean>(() => !!shipment.value?.deliveryAddress);
  const isValidBillingAddress = computed<boolean>(
    () => billingAddressEqualsShipping.value || !!payment.value?.billingAddress,
  );
  const isValidShipmentMethod = computed<boolean>(() => !!shipment.value?.shipmentMethodCode);
  const isValidPaymentMethod = computed<boolean>(() => !!payment.value?.paymentGatewayCode);
  const isValidShipment = computed<boolean>(() => isValidDeliveryAddress.value && isValidShipmentMethod.value);
  const isValidPayment = computed<boolean>(() => isValidBillingAddress.value && isValidPaymentMethod.value);
  const isValidCheckout = computed<boolean>(
    () => isValidShipment.value && isValidPayment.value && !hasValidationErrors.value,
  );

  const selectedPaymentMethodGroupType = computed<string | undefined>(() => {
    const paymentMethodCode = placedOrder.value?.inPayments[0].paymentMethod?.code ?? payment.value?.paymentGatewayCode;
    return availablePaymentMethods.value.find((item) => item.code === paymentMethodCode)?.paymentMethodGroupType;
  });

  const canPayNow = computed<boolean>(
    () =>
      isAuthenticated.value &&
      !!selectedPaymentMethodGroupType.value &&
      selectedPaymentMethodGroupType.value !== PaymentMethodGroupType[PaymentMethodGroupType.Manual],
  );

  const addresses = computed<MemberAddressTypeExtension[]>(() => {
    return personalAddresses.value;
  });

  const allOrderItemsAreDigital = computed(() =>
    placedOrder.value?.items?.every((item) => item.productType === ProductType.Digital),
  );

  const hasTaxExemptNo = computed(() => {
    return shipment.value?.deliveryAddress?.taxExemptNoTL || false;
  });

  async function setDeliveryAddress(address: MemberAddressTypeExtension): Promise<void> {
    address.dataAreaIdTL = getDataAreaId(globals.currencyCode, address.countryCode!);
    await updateShipment({
      id: shipment.value?.id,
      shippingAccountNumberTL: shipment.value?.shippingAccountNumberTL ?? "",
      wantsFewerPackagesTL: shipment.value?.wantsFewerPackagesTL ?? false,
      businessDaysToShipTL: shipment.value?.businessDaysToShipTL ?? "",
      deliveryAddress: {
        ...omit(address, [
          "id",
          "isDefault",
          "description",
          "isFavorite",
          "isDefaultShippingAddressTL",
          "isDefaultBillingAddressTL",
          "modifiedDate",
          "createdDate",
        ]),
        outerId: address.id,
        addressType: AddressType.Shipping,
      },
    });

    userSelectedAddress.value = address.id;
  }

  async function setShippingMethod(
    method: ShippingMethodType,
    wantsFewerPackages?: boolean,
    shippingAccountNumber?: string,
    shipmentComment?: string,
    businessDaysToShipTL?: string,
    rateModeId?: string,
    // For punchout case / flow we need to send a mock deliveryAddress for correctly validating the shipment details in backend
    // For other cases the value is optional and it will not be sent to backend, therefore the deliveryAddress will not be saved / updated
    newDeliveryAddress?: MemberAddressTypeExtension,
    // For punchout case / flow we need to send the shipping currency for correctly validating the shipment details in backend, as there is no cart shipment yet
    // For other cases the value is optional and it will not be sent to backend, therefore the shipping currency will not be saved / updated
    shipmentCurrency?: string,
  ): Promise<void> {
    await updateShipment({
      id: shipment.value?.id,
      price: method.price?.amount,
      shipmentMethodCode: method.code,
      shipmentMethodOption: method.optionName,
      wantsFewerPackagesTL: wantsFewerPackages,
      shippingAccountNumberTL: shippingAccountNumber,
      vendorId: rateModeId,
      comment: shipmentComment,
      weight: cartWeight.value,
      weightUnit: "kg",
      businessDaysToShipTL: businessDaysToShipTL?.toString(),
      deliveryAddress: newDeliveryAddress,
      currency: shipmentCurrency,
    });
  }

  async function setPaymentMethod(method: PaymentMethodType): Promise<void> {
    // Update payment with required properties
    const filledPayment: InputPaymentTypeExtension = {
      id: payment.value!.id,
      paymentGatewayCode: method.code,
    };

    // Save shipping address as billing address
    if (billingAddressEqualsShipping.value) {
      filledPayment.billingAddress = {
        ...omit(shipment.value!.deliveryAddress, ["id"]),
        outerId: shipment.value?.deliveryAddress?.outerId,
        addressType: AddressType.Billing,
      };
    }

    await updatePayment(filledPayment);
  }

  function loadShipmentMethodTopNote() {
    const { contentResult, slugInfo, fetchContent, hasContent } = useSlugInfo("ShippingMethodNote");
    watch(slugInfo, async () => {
      if (hasContent.value) {
        await fetchContent();
      }

      if (contentResult.value?.page?.content) {
        const keyData = isMobile.value ? `${regionCurrency.value}DataMobile` : `${regionCurrency.value}Data`;
        const shippingMethodBlock = JSON.parse(contentResult.value.page.content) as IShippingMethodBlock;
        const currencyKey = Object.keys(shippingMethodBlock).find(
          (key) => key.toLowerCase() == keyData.toLowerCase(),
        ) as keyof IShippingMethodBlock;

        shipmentMethodTopNote.value = shippingMethodBlock[currencyKey] as string;
      }
    });
  }

  async function setCartDeliveryAddress(address: MemberAddressTypeExtension, currentPaymentMethod?: string) {
    if (globals.currencyCode === "USD") {
      address.dataAreaIdTL = getDataAreaId(globals.currencyCode, address.countryCode!, currentPaymentMethod);
      // If there will be an existing shipment ( previously stored shipment ), its details will be fetched directly from the database in backend
      // There is no need to send back the shipment details as user input when setting the cartDeliveryAddress ( have the non-USD case as example )
      const inputShipmentMethod = {
        id: shipment.value?.id,
        wantsFewerPackagesTL: shipment.value?.wantsFewerPackagesTL ?? false,
        shippingAccountNumberTL: shipment.value?.shippingAccountNumberTL ?? "",
        businessDaysToShipTL: shipment.value?.businessDaysToShipTL ?? "",
        deliveryAddress: {
          ...omit(address, [
            "id",
            "isDefault",
            "description",
            "isFavorite",
            "isDefaultShippingAddressTL",
            "isDefaultBillingAddressTL",
            "modifiedDate",
            "createdDate",
          ]),
          outerId: address.id,
          addressType: AddressType.Shipping,
        },
      } as InputShipmentTypeExtension;

      await updateUsdSalesTax({
        storeId: globals.storeId,
        currencyCode: globals.currencyCode,
        cultureName: globals.cultureName,
        cartId: cart.value?.id,
        userId: globals.userId,
        shipment: inputShipmentMethod,
      });

      // Set the user selected address for explicitly triggering the radio button checked state and UI
      // Same assignment that is happening for non-USD case in setDeliveryAddress function
      userSelectedAddress.value = address.id;
    } else {
      await setDeliveryAddress(address);
    }

    // Trigger the deliveryAddress list re-render with correct radiobutton checked state
    deliveryAddressListUpdaterKey.value++;
  }

  async function initialize(): Promise<void> {
    placedOrder.value = null;
    loading.value = true;
    // Call this first, before any async operations, so it's in the proper setup context
    loadShipmentMethodTopNote();

    await checkoutInitialize();

    await fetchAddresses();
    // ga.beginCheckout({ .. .cart.value!, items: selectedLineItems.value });

    if (!countriesWithoutEmbargoed.value.length) {
      await loadCountriesWithoutEmbargoed();
    }

    if (shipment.value?.deliveryAddress) {
      const autoSelectedAddress = addresses.value.find((item) =>
        isEqualAddresses(item, Object.assign({}, shipment.value!.deliveryAddress!), { compareERPIdTL: true }),
      );
      userSelectedAddress.value = autoSelectedAddress?.id;

      // this means that the address has been deleted in the meantime
      if (!autoSelectedAddress) {
        const newAddress = addresses.value.find((item) => item.isDefaultShippingAddressTL) || addresses.value[0];

        userSelectedAddress.value = newAddress?.id;

        // Only call setCartDeliveryAddress if we have a valid address
        if (newAddress) {
          await setCartDeliveryAddress(newAddress);
        }
      }
    }
    loading.value = false;
  }

  async function initializeQuoteCheckout(quoteId: string) {
    placedOrder.value = null;
    loading.value = true;

    await fetchFullOrder({ id: quoteId });
    await fetchAddresses();
    if (!countriesWithoutEmbargoed.value.length) {
      await loadCountriesWithoutEmbargoed();
    }

    if (quoteOrder.value) {
      const result = await getAvailability(quoteOrder.value.items);
      customerOrderAvailabilityItems.value = quoteOrder.value.items.map((item) => {
        let status = result?.find((x) => x.productId === item.id)?.availabilityResponse?.availability ?? "";

        if (status && status === "None") {
          status = t("shared.product.request_lead_time");
        }

        return <OrderLineItemWithAvailabilityStatus>{
          lineItem: item,
          status,
          productTitle: item.product?.mainProductId
            ? item.product?.masterVariation?.slug
            : item.product?.isConfigurable
              ? item.name
              : item.sku,
        };
      });
    }

    checkoutMode.value = "CustomerOrder";

    loading.value = false;
  }

  const orderLineItemsDiscountTotal = computed(() =>
    sumBy<OrderLineItemTypeExtension>(quoteOrder.value?.items ?? [], (item) => item.discountTotal?.amount ?? 0),
  );
  const quoteOrderHasDiscounts = computed(
    () => quoteOrder!.value!.discounts?.length || orderLineItemsDiscountTotal.value > 0,
  );
  const quoteOrderHasOnlyNewLabDiscount = computed(() =>
    quoteOrder!
      .value!.items.filter((item) => item.discounts?.length > 0)
      .every((item) => item.discounts.find((d) => d.description?.toLowerCase() === "new lab discount")),
  );

  async function updateBillingOrDeliveryAddress(
    addressType: AddressType,
    inputAddress: InputAddressTypeExtension,
  ): Promise<void> {
    if (
      addressType === AddressType.Billing &&
      (!payment.value?.billingAddress || !isEqualAddresses(payment.value?.billingAddress, inputAddress))
    ) {
      await updatePayment({
        id: payment.value?.id,
        billingAddress: inputAddress,
      });
    } else if (
      addressType === AddressType.Shipping &&
      (!shipment.value?.deliveryAddress || !isEqualAddresses(shipment.value?.deliveryAddress, inputAddress))
    ) {
      await updateShipment({
        id: shipment.value?.id,
        deliveryAddress: inputAddress,
      });
    }
  }

  async function fetchAddresses(): Promise<void> {
    if (!isAuthenticated.value) {
      return;
    }
    await fetchPersonalAddresses();
  }

  // Covers both timer-pending and in-flight debounced cart writes, preventing concurrent cart saves.
  async function waitForPendingCartWrites(): Promise<void> {
    await Promise.allSettled([
      commentUpdate.value,
      purchaseOrderNumberUpdate.value,
      intendsToExportUpdate.value,
      wantsInvoicePdfUpdate.value,
      wantsProformaInvoiceUpdate.value,
    ]);
  }

  async function prepareOrderData(): Promise<void> {
    await waitForPendingCartWrites();

    // Resend gateway code and billing address so a stale server-side cart snapshot cannot wipe them.
    const filledPayment: InputPaymentTypeExtension = {
      id: payment.value!.id,
      amount: cart.value!.total.amount, // required
      paymentGatewayCode: payment.value!.paymentGatewayCode,
    };

    const billingAddressSource = billingAddressEqualsShipping.value
      ? shipment.value?.deliveryAddress
      : payment.value?.billingAddress;
    if (billingAddressSource) {
      filledPayment.billingAddress = {
        ...omit(billingAddressSource, ["id"]),
        outerId: billingAddressSource.outerId,
        addressType: AddressType.Billing,
      };
    }

    await updatePayment(filledPayment);
  }

  const INR_INDIA_SHIPPING_ERROR_CODE = "INR_INDIA_SHIPPING_ERROR";
  const INDIA_INR_CURRENCY_ERROR_CODE = "INDIA_INR_CURRENCY_ERROR";

  // The backend returns error codes instead of user-facing text; map known codes to localized messages.
  const localizeOrderCreationError = (rawErrorMessage?: string | null): string | undefined => {
    if (rawErrorMessage?.includes(INR_INDIA_SHIPPING_ERROR_CODE)) {
      return t("pages.checkout.inr_india_shipping_error");
    }
    if (rawErrorMessage?.includes(INDIA_INR_CURRENCY_ERROR_CODE)) {
      return t("pages.cart.india_inr_repricing_message");
    }
    return undefined;
  };

  async function createOrderFromCart(): Promise<CustomerOrderTypeExtension | null> {
    loading.value = true;

    try {
      await prepareOrderData();
    } catch (e) {
      Logger.error(`${useCheckout.name}.${createOrderFromCart.name}: preparing order data failed`, e);
      void logClientInformation({
        title: "Checkout.prepareOrderDataFailed",
        content: `prepareOrderData failed before createOrderFromCart. cartId=${cart.value?.id ?? "unknown"}, error=${e instanceof Error ? e.message : String(e)}`,
        withTelemetry: true,
        userId: user.value?.memberId ?? user.value?.id ?? "unknown",
      }).catch(() => {});
      loading.value = false;
      return null;
    }

    // Persist the reseller preference before creating the order so the backend EUS/AutoProcess
    // evaluation reads the up-to-date IsResellerTL flag from the contact.
    await finalizeResellerPreference();

    let hasPaymentGatewayMissingError = false;
    let isCartProductUnavailable = false;
    let orderError: unknown;
    try {
      placedOrder.value = (await _createOrderFromCart(cart.value!.id)) as CustomerOrderTypeExtension;
    } catch (e) {
      Logger.error(`Create order failed - ${useCheckout.name}.${createOrderFromCart.name}`, e);
      orderError = e;
      hasPaymentGatewayMissingError = isErrorWithCode(e, GraphQLErrorCode.PaymentGatewayMissing);
      isCartProductUnavailable = isCartProductUnavailableError(e);
    }

    if (placedOrder.value) {
      try {
        if (isRequestQuoteFlow.value) {
          gtmSendQuoteEvent(placedOrder.value);
        } else {
          gtmSendPurchaseEvent(placedOrder.value);
        }

        await algoliaSendPurchasedObjectIDsAfterSearch(placedOrder.value);
        // ga.placeOrder(placedOrder.value);
      } catch (e) {
        Logger.error("Sending analytic events after order creation has failed", e);
      }

      if (themeContext.value?.storeSettings?.defaultSelectedForCheckout) {
        selectedItemIds.value = cart.value!.items.map((item) => item.id);
      }

      clearState();

      await router.replace({ name: "CheckoutCompleted" });
    } else if (isCartProductUnavailable) {
      await router.replace({ name: "Cart" });
    } else if (hasPaymentGatewayMissingError) {
      // Mirrors the credit-card authorization failure path: send the user back to re-save payment.
      eventBus.emit("expandCheckoutComponent", { initPaymentForm: false });
      eventBus.emit("showCreditCardFormError", { message: t("common.messages.payment_method_missing_error") });
    } else if (notifyBrlCurrencyOrderError(orderError)) {
      // The backend rejected the BRL cart; its message has been shown to the user.
    } else {
      notifications.error({
        text: t("common.messages.creating_order_error"),
        duration: 15000,
        single: true,
      });
    }

    loading.value = false;

    return placedOrder.value;
  }

  async function createOrderFromQuote(): Promise<CreateOrderWithPaymentAuthorizationResultType | null> {
    loading.value = true;
    let response = null;
    let hasEmittedDialog = false;

    // Persist the reseller preference before creating the order so the backend EUS/AutoProcess
    // evaluation reads the up-to-date IsResellerTL flag from the contact.
    await finalizeResellerPreference();

    try {
      response = await _createOrderFromQuote(quoteOrder.value!.id);
      if (response?.isSuccess) {
        placedOrder.value = response.order as CustomerOrderTypeExtension;
      } else {
        Logger.error(`${useCheckout.name}.${_createOrderFromQuote.name}`, response?.errorMessage);
        eventBus.emit("showOrderCreationFailureDialog", {
          errorMessage: localizeOrderCreationError(response?.errorMessage) ?? response?.errorMessage,
        });
        hasEmittedDialog = true;
      }
    } catch (e) {
      Logger.error(`${useCheckout.name}.${_createOrderFromQuote.name}`, e);
      const rawErrorMessage = e instanceof Error ? e.message : "Unknown error";
      eventBus.emit("showOrderCreationFailureDialog", {
        errorMessage: localizeOrderCreationError(rawErrorMessage) ?? rawErrorMessage,
      });
      hasEmittedDialog = true;
    }

    if (placedOrder.value) {
      try {
        gtmSendPurchaseEvent(placedOrder.value);
        await algoliaSendPurchasedObjectIDsAfterSearch(placedOrder.value);
        // ga.placeOrder(placedOrder.value);
      } catch (e) {
        Logger.error("Sending analytic events after order creation has failed", e);
      }

      clearState();

      await router.replace({ name: "CheckoutCompleted" });
    } else if (!hasEmittedDialog) {
      // Only show notification if we haven't already shown the custom dialog
      notifications.error({
        text: t("common.messages.creating_order_error"),
        duration: 15000,
        single: true,
      });
    }

    loading.value = false;

    return response as CreateOrderWithPaymentAuthorizationResultType;
  }

  async function createOrderFromCartWithPaymentAuthorization(
    parameters: KeyValueType[],
  ): Promise<CreateOrderWithPaymentAuthorizationResultType | OrderErrorHandledType | null> {
    loading.value = true;

    try {
      await prepareOrderData();
    } catch (e) {
      Logger.error(
        `${useCheckout.name}.${createOrderFromCartWithPaymentAuthorization.name}: preparing order data failed`,
        e,
      );
      void logClientInformation({
        title: "Checkout.prepareOrderDataFailed",
        content: `prepareOrderData failed before createOrderFromCartWithPaymentAuthorization. cartId=${cart.value?.id ?? "unknown"}, error=${e instanceof Error ? e.message : String(e)}`,
        withTelemetry: true,
        userId: user.value?.memberId ?? user.value?.id ?? "unknown",
      }).catch(() => {});
      loading.value = false;
      return null;
    }

    // Persist the reseller preference before creating the order so the backend EUS/AutoProcess
    // evaluation reads the up-to-date IsResellerTL flag from the contact.
    await finalizeResellerPreference();

    let response = null;
    let isCartProductUnavailable = false;
    let isBrlCurrencyRejected = false;
    try {
      response = await _createOrderFromCartWithPaymentAuthorization(cart.value!.id, parameters);

      if (response?.isSuccess) {
        placedOrder.value = response.order as CustomerOrderTypeExtension;
      } else {
        Logger.error(`${useCheckout.name}.${createOrderFromCartWithPaymentAuthorization.name}`, response?.errorMessage);
      }
    } catch (e) {
      Logger.error(`${useCheckout.name}.${createOrderFromCartWithPaymentAuthorization.name}`, e);
      isCartProductUnavailable = isCartProductUnavailableError(e);
      isBrlCurrencyRejected = notifyBrlCurrencyOrderError(e);
    }

    if (placedOrder.value) {
      try {
        if (isRequestQuoteFlow.value) {
          gtmSendQuoteEvent(placedOrder.value);
        } else {
          gtmSendPurchaseEvent(placedOrder.value);
        }
        await algoliaSendPurchasedObjectIDsAfterSearch(placedOrder.value);
        // ga.placeOrder(placedOrder.value);
      } catch (e) {
        Logger.error("Sending analytic events after order creation has failed", e);
      }

      clearState();
    }

    if (isCartProductUnavailable) {
      loading.value = false;
      await router.replace({ name: "Cart" });
      // Distinct from a normal result so the facade does not also run payment-error handling.
      return ORDER_ERROR_HANDLED;
    }

    if (isBrlCurrencyRejected) {
      loading.value = false;
      return ORDER_ERROR_HANDLED;
    }

    const is478Error = response?.errorMessage?.includes("478-ERROR");
    const isStepUpRequired = !response?.isSuccess && !!response?.accessToken && !!response?.stepUpUrl;
    if ((is478Error && globals.convertCc478OrdersToNetTerms) || isStepUpRequired) {
      return response as CreateOrderWithPaymentAuthorizationResultType;
    } else {
      loading.value = false;
    }

    return response as CreateOrderWithPaymentAuthorizationResultType;
  }

  async function createOrderFromQuoteWithPaymentAuthorization(
    parameters: KeyValueType[],
  ): Promise<CreateOrderWithPaymentAuthorizationResultType | null> {
    loading.value = true;

    let response = null;
    let hasEmittedDialog = false;

    // Persist the reseller preference before creating the order so the backend EUS/AutoProcess
    // evaluation reads the up-to-date IsResellerTL flag from the contact.
    await finalizeResellerPreference();

    try {
      response = await _createOrderFromQuoteWithPaymentAuthorization(quoteOrder.value!.id, parameters);

      if (response?.isSuccess) {
        placedOrder.value = response.order as CustomerOrderTypeExtension;
      } else {
        Logger.error(
          `${useCheckout.name}.${createOrderFromQuoteWithPaymentAuthorization.name}`,
          response?.errorMessage,
        );
        const isStepUpRequired = !!response?.accessToken && !!response?.stepUpUrl;
        if (!isStepUpRequired) {
          eventBus.emit("showOrderCreationFailureDialog", {
            errorMessage: localizeOrderCreationError(response?.errorMessage) ?? response?.errorMessage,
          });
          hasEmittedDialog = true;
        }
      }
    } catch (e) {
      Logger.error(`${useCheckout.name}.${createOrderFromQuoteWithPaymentAuthorization.name}`, e);
      // Emit event to show failure dialog for exceptions
      const rawErrorMessage = e instanceof Error ? e.message : "Unknown error";
      eventBus.emit("showOrderCreationFailureDialog", {
        errorMessage: localizeOrderCreationError(rawErrorMessage) ?? rawErrorMessage,
      });
      hasEmittedDialog = true;
    }

    if (placedOrder.value) {
      try {
        gtmSendPurchaseEvent(placedOrder.value);
        await algoliaSendPurchasedObjectIDsAfterSearch(placedOrder.value);
        // ga.placeOrder(placedOrder.value);
      } catch (e) {
        Logger.error("Sending analytic events after order creation has failed", e);
      }

      clearState();
    } else if (!hasEmittedDialog) {
      // Only show notification if we haven't already shown the custom dialog
      const isStepUpRequired = !response?.isSuccess && !!response?.accessToken && !!response?.stepUpUrl;
      if (!isStepUpRequired) {
        notifications.error({
          text: t("common.messages.creating_order_error"),
          duration: 15000,
          single: true,
        });
      }
    }

    const isStepUpRequired = !response?.isSuccess && !!response?.accessToken && !!response?.stepUpUrl;
    if (!isStepUpRequired) {
      loading.value = false;
    }

    return response as CreateOrderWithPaymentAuthorizationResultType;
  }
  function clearState() {
    clearGlobalCheckoutState();
  }

  function stopLoading() {
    loading.value = false;
  }

  return {
    deliveryAddress,
    loadingShippingChoices,
    hasCheckoutCompleted,
    hasCompletedExportStep,
    shipmentMethod,
    shipmentMethodTopNote,
    addresses,
    billingAddress,
    paymentMethod: computed(() => paymentMethod.value),
    paymentMethodDetailsForReview,
    isPaymentMethodExpanded,
    selectedPaymentMethod,
    comment,
    billingAddressEqualsShipping,
    purchaseOrderNumber,
    purchaseOrderNumberUpdate,
    intendsToExport,
    wantsInvoicePdf,
    wantsProformaInvoice,
    shippingAccountNumberRecalculateTotals,
    updateBillingOrDeliveryAddress,
    isValidDeliveryAddress,
    isValidBillingAddress,
    isValidShipmentMethod,
    isValidPaymentMethod,
    isValidShipment,
    isValidPayment,
    isValidCheckout,
    selectedPaymentMethodGroupType,
    canPayNow,
    initialize,
    initializeQuoteCheckout,
    userSelectedAddress,
    userSelectedBillingAddress,
    hasTaxExemptNo,
    isRequestQuoteFlow,
    setShippingMethod,
    setDeliveryAddress,
    setPaymentMethod,
    createOrderFromCart,
    createOrderFromQuote,
    createOrderFromCartWithPaymentAuthorization,
    createOrderFromQuoteWithPaymentAuthorization,
    updateUsdSalesTax,
    setCartDeliveryAddress,
    waitForPendingCartWrites,
    deliveryAddressListUpdaterKey: readonly(deliveryAddressListUpdaterKey),
    loading: readonly(loading),
    stopLoading,
    changing: computed(
      () => shippingAccountNumberChanging.value || loadingShippingChoices.value || calculateUsdSalesTaxChanging.value,
    ),
    placedOrder: computed(() => placedOrder.value),
    allOrderItemsAreDigital,
    hasExplicitlyCalculatedTax: readonly(hasExplicitlyCalculatedTax),
    checkoutMode: computed(() => checkoutMode.value),
    quoteOrder: computed(() => quoteOrder.value),
    customerOrderIntendsToExport,
    customerOrderComment,
    customerOrderWantsInvoicePdf,
    customerOrderWantsProformaInvoice,
    customerOrderPurchaseOrderNumber,
    customerOrderPurchaseOrderNumberUpdate,
    customerOrderAvailabilityItems,
    quoteOrderHasDiscounts,
    quoteOrderHasOnlyNewLabDiscount,
  };
}

export const useCheckout = createSharedComposable(_useCheckout);
